"use client";

import React, { useState, useEffect } from "react";
import { Search, ArrowUpDown } from "lucide-react";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { EcommerceTermsProvider, useEcommerceTerms } from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Skeleton } from "@/components/ui/skeleton";
function HorizontalCategoryFilter({
  selectedCategoryId,
  onSelectCategory
}: {
  selectedCategoryId: number | null;
  onSelectCategory: (id: number | null) => void;
}) {
  const {
    terms,
    loading
  } = useEcommerceTerms();
  if (loading) {
    return <div className="flex gap-2 overflow-x-auto pb-4 no-scrollbar">
        {[...Array(5)].map((_, i) => <Skeleton key={i} data-index={i} className="h-9 w-24 rounded-md shrink-0" />)}
      </div>;
  }
  return <div className="flex gap-2 overflow-x-auto pb-4 no-scrollbar mb-6">
      {terms.map((term, i) => <Button key={term.id} data-index={i} variant={selectedCategoryId === term.id ? "default" : "outline"} onClick={() => onSelectCategory(term.id)} className="rounded-md whitespace-nowrap">
          {term.name}
        </Button>)}
    </div>;
}
function ProductsGridConsumer() {
  const {
    products,
    loading,
    error,
    setSorting,
    hasNext,
    nextPage,
    currentPage,
    totalPages
  } = useEcommerceProducts();
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const handleSort = (value: string) => {
    setSortBy(value);
    setSorting(value, sortOrder);
  };
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="h-96 rounded-lg bg-muted/30 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground mb-4">Failed to load smart solutions.</p>
        <Button variant="outline" className="border-primary text-primary hover:bg-accent rounded-md">Retry</Button>
      </div>;
  }
  if (products.length === 0) {
    return <div className="text-center py-12">
        <p className="text-muted-foreground text-lg font-default">No smart systems found. Try adjusting your search or filters.</p>
      </div>;
  }
  return <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-[80%] mx-auto">
        {products.map((product, i) => <Link key={product.id} data-index={i} to={product.link} className="group block overflow-hidden rounded-lg border border-border shadow-md bg-card transition-all hover:shadow-lg">
            <div className="aspect-square overflow-hidden bg-muted">
              <img src={product.featured_image_url || product.featured_image} alt={product.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
            </div>
            <div className="p-4 flex flex-col items-center text-center space-y-3">
              <h3 className="font-semibold text-foreground text-lg line-clamp-1">
                {product.title}
              </h3>
              <Button variant="default" className="w-full rounded-md font-medium">
                Contact Us
              </Button>
            </div>
          </Link>)}
      </div>

      {totalPages > 1 && <div className="flex justify-center gap-4 pt-6">
          <Button variant="outline" className="border-border text-foreground rounded-md" onClick={() => window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })} disabled={currentPage === 1}>
            Previous
          </Button>
          <span className="flex items-center text-sm text-muted-foreground font-default">
            Page {currentPage} of {totalPages}
          </span>
          <Button className="bg-primary text-primary-foreground rounded-md" onClick={() => {
        nextPage();
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }} disabled={!hasNext}>
            Next
          </Button>
        </div>}
    </div>;
}
function OurProductsContent({
  searchValue,
  setSearchValue,
  selectedCategoryId,
  setSelectedCategoryId
}: {
  searchValue: string;
  setSearchValue: (val: string) => void;
  selectedCategoryId: number | null;
  setSelectedCategoryId: (id: number | null) => void;
}) {
  const {
    terms
  } = useEcommerceTerms();
  useEffect(() => {
    if (terms.length > 0 && selectedCategoryId === null) {
      setSelectedCategoryId(terms[0].id);
    }
  }, [terms, selectedCategoryId, setSelectedCategoryId]);
  const [wp_query_base] = useWPQueryForSearch(searchValue);
  const wp_query = new WP_Query({
    ...wp_query_base.query_vars,
    ...(selectedCategoryId ? {
      product_cat: selectedCategoryId.toString()
    } : {}),
    ...(searchValue.trim() === "" && selectedCategoryId ? {
      per_page: 12,
      s: undefined
    } : {})
  });
  return <>
      <HorizontalCategoryFilter selectedCategoryId={selectedCategoryId} onSelectCategory={setSelectedCategoryId} />
      
      <EcommerceProductsProvider wp_query={wp_query} className="space-y-6">
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex-1">
            <ProductsSearch value={searchValue} onChange={setSearchValue} placeholder="Search smart systems..." className="rounded-md border-border focus:ring-primary" />
          </div>
          <Select defaultValue="date">
            <SelectTrigger className="w-full md:w-48 border-border rounded-md bg-card text-foreground">
              <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent className="bg-popover border-border">
              <SelectItem value="date" className="font-default">Latest Release</SelectItem>
              <SelectItem value="price" className="font-default">Price: Low to High</SelectItem>
              <SelectItem value="popularity" className="font-default">Most Deployed</SelectItem>
              <SelectItem value="title" className="font-default">Alphabetical</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <ProductsGridConsumer />
      </EcommerceProductsProvider>
    </>;
}
export default function OurProducts() {
  const [searchValue, setSearchValue] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const term_query = new WP_Term_Query({
    taxonomy: "product_cat",
    per_page: 100
  });
  return <section data-section-id="252893" id="productcategoryfilter" className="bg-background text-foreground py-12 md:py-16 lg:py-20 light">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold font-default mb-4 text-foreground tracking-tight">Smart Product Catalog</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto font-default">Explore our high-performance industrial software and hardware integration solutions.</p>
        </div>

        <EcommerceTermsProvider wp_term_query={term_query}>
          <OurProductsContent searchValue={searchValue} setSearchValue={setSearchValue} selectedCategoryId={selectedCategoryId} setSelectedCategoryId={setSelectedCategoryId} />
        </EcommerceTermsProvider>
      </div>
    </section>;
}